import type { ReactNode } from 'react';
import { useAppState } from '../../app/state';
import { callAction } from '../../app/actions';

/**
 * ONE FOLD SHAPE for every section of the node popup (V0.51).
 *
 * WHAT IT REPLACES. The popup had grown a section per release — name, colour, description,
 * responsibles, documentation plan, four doc slots, the widget address, to-dos, the AI panel,
 * the tag wall — and read as a stack of fields rather than as a page about a node. Two of those
 * already folded (`TodoSection`, `AiPanel`), each with its own `<details>`, its own open rule
 * and its own storage. This is that shape lifted out and made the rule: Responsibles,
 * Documentation and Tags fold too, and all five answer to one implementation.
 *
 * SHUT BY DEFAULT, AND WHAT YOU OPEN STAYS OPEN — the user's decision, and the whole point.
 * `S.openSections` holds what this user has opened; a fold opened on one node is open on the
 * NEXT node opened, because somebody working through documentation wants that section on every
 * node they visit and re-opening it per node is friction with no purpose. Per user, day-scoped
 * (`ViewRecord.openSections`), so a new work day starts everything shut again — which is the
 * rule's own default rather than a second policy.
 *
 * This deliberately REPLACES two earlier answers, and neither should be reinstated by accident:
 *  - `TodoSection`'s V0.50 rule derived the default from the CONTENTS (open when the node had
 *    to-dos). It made "is this section open?" a question about the node instead of about the
 *    reader, and needed a `touched` flag to stop an unfolded empty section re-folding itself.
 *  - `AiPanel` remembered itself in `sessionStorage`, per TAB. One popup, two lifetimes.
 *
 * THE SUMMARY IS THE SECTION'S ANSWER, and it changes with the fold:
 *  - CLOSED: the items themselves, as their own pills — tag chips in the tag's colour, doc
 *    pills in the kind's (dashed when the page is owed), people tokens with their avatar. Four
 *    at most, then one "+n" pill wearing the same class as its neighbours, so the overflow
 *    reads as one more of the same thing rather than as a footnote.
 *  - OPEN: one count pill instead. The items are on screen below; repeating them in the heading
 *    is noise, and a count is the one thing the heading can still say that the body does not.
 *  - `count` is drawn in BOTH states and is To-dos' own `1/3` — the one figure somebody opens
 *    that section for, and the reason it was a `.pp-foldcount` before this existed.
 *
 * Every value sits at the RIGHT EDGE of the summary (`.pp-foldval{margin-left:auto}`), which is
 * where the To-dos count has been since V0.50.1; the arrangement was chosen against the
 * alternative in a proof of concept.
 */

/** Four, then "+n". Not a tuning knob: it is what fits one summary line at the popup's width. */
export const FOLD_PILL_CAP = 4;

export interface FoldSummary {
  /** The pills, in the order they should read. Capped; the remainder becomes one "+n". */
  pills: ReactNode[];
  /**
   * The class the "+n" pill wears — `'chip mini'`, `'docpill mini'`, `'subj-tok mini'`. The
   * caller's, because the overflow must be the same shape as the things it stands for, and
   * only the caller knows which shape those were.
   */
  plusClass: string;
}

export interface PopupFoldProps {
  /** A key from `./sections`. What `S.openSections` holds. */
  sectionKey: string;
  /** The uppercase heading, in the `<label>` voice the rest of the popup uses. */
  label: string;
  /** Drawn while SHUT. Absent for a section with nothing to summarise. */
  summary?: FoldSummary;
  /** Drawn while OPEN, in the summary's place — one count pill. */
  brief?: ReactNode;
  /** Drawn in BOTH states, after the value. To-dos' `1/3`. */
  count?: ReactNode;
  /** The `<details>` id, where a spec drives one (`pp-todos`, `pp-aipanel`). */
  id?: string;
  /** The `<summary>` id, likewise (`pp-todos-summary`). */
  summaryId?: string;
  /** Extra classes on the `<details>` — `'todopanel'`, `'sub'`. `.aipanel` is always there. */
  className?: string;
  /** Extra classes on the body — `'todolist'`. `.aipanel-body` is always there. */
  bodyClassName?: string;
  children: ReactNode;
}

/** The pills, capped, with the overflow as one more pill of the same shape. */
function cappedPills({ pills, plusClass }: FoldSummary): ReactNode {
  if (pills.length <= FOLD_PILL_CAP) return pills;
  const rest = pills.length - FOLD_PILL_CAP;
  return (
    <>
      {pills.slice(0, FOLD_PILL_CAP)}
      <span className={`${plusClass} plus`} title={`${rest} more`}>
        +{rest}
      </span>
    </>
  );
}

export function PopupFold({
  sectionKey,
  label,
  summary,
  brief,
  count,
  id,
  summaryId,
  className,
  bodyClassName,
  children,
}: PopupFoldProps) {
  const s = useAppState();
  const open = s.openSections.has(sectionKey);

  return (
    <details
      className={`aipanel${className ? ` ${className}` : ''}`}
      id={id}
      open={open}
      /* The equality guard is for the ECHO: `open` is a controlled prop, so React re-asserting
         the value React itself just rendered fires `toggle` a second time. Without it every
         open writes the record twice. Lifted from `TodoSection` (V0.50), where the same
         controlled-`<details>` problem was solved once already. */
      onToggle={(e) => {
        const next = e.currentTarget.open;
        if (next === open) return;
        callAction('setSectionOpen', sectionKey, next);
      }}
    >
      <summary id={summaryId}>
        <span>{label}</span>
        {summary && summary.pills.length ? (
          <span className="pp-foldval full">{cappedPills(summary)}</span>
        ) : null}
        {brief ? <span className="pp-foldval brief">{brief}</span> : null}
        {count ? <span className="pp-foldval count">{count}</span> : null}
      </summary>
      <div className={`aipanel-body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>
    </details>
  );
}

/** The count pill every `brief` is made of, and To-dos' `count`. One shape, one place. */
export function CountPill({ children }: { children: ReactNode }) {
  return <span className="count-pill">{children}</span>;
}
